"use client";

import { useEffect, useMemo, useState } from "react";
import type { BlogPost, BlogField } from "@/lib/blog";
import { FIELDS } from "@/lib/blog";
import PostCard from "@/components/blog/PostCard";

const FIELD_IDS = FIELDS.map((f) => f.id);

function isBlogField(value: string | null): value is BlogField {
  return !!value && (FIELD_IDS as string[]).includes(value);
}

export default function BlogExplorer({ posts }: { posts: BlogPost[] }) {
  const [query, setQuery] = useState("");
  const [field, setField] = useState<BlogField | "All">("All");

  // Plain client-side effect (not useSearchParams) so the full unfiltered grid
  // still renders in the static HTML for crawlers — only after hydration do we
  // check the URL and apply a ?field= filter, matching the breadcrumb links.
  useEffect(() => {
    const initialField = new URLSearchParams(window.location.search).get("field");
    if (isBlogField(initialField)) setField(initialField);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const matchesField = field === "All" || p.field === field;
      const matchesQuery =
        !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q));
      return matchesField && matchesQuery;
    });
  }, [posts, query, field]);

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setField("All")}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              field === "All"
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "border border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
            }`}
          >
            All
          </button>
          {FIELDS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setField(f.id)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                field === f.id
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "border border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
              }`}
            >
              {f.icon} {f.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64">
          <svg
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles..."
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-700 focus:border-cyan-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
          />
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
        {filtered.length} article{filtered.length === 1 ? "" : "s"}
        {field !== "All" ? ` in ${FIELDS.find((f) => f.id === field)?.label}` : ""}
      </p>

      {filtered.length ? (
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      ) : (
        <p className="mt-10 text-center text-sm text-slate-500 dark:text-slate-400">
          No articles match your filters yet.
        </p>
      )}
    </div>
  );
}
